import { useEffect, useState, useMemo } from "react";
import { getHolidayDates } from "@utils/holiday";
import { useLoginUser } from "@hooks/useAuth";
import ModalWeeklyOffTableHeader from "../rosterTable/ModalWeeklyOffTableHeader";
import ModalWeeklyOffTableBodyView from "../rosterTable/ModalWeeklyOffTableBodyView";
import { useQuery } from "@tanstack/react-query";
import * as req from "@script/req";
import { X } from "lucide-react";
import type { NurseResponse } from "@/types/nurse-type";
import { weeklyOffNurses } from "@api/nurses";
import CustomModal from "@/components/modal/CustomModal";

interface ModalWeekOffFullNurseProps {
  onClose: () => void;
  weeklyOffSetYear?: number;
  weeklyOffSetMonth?: number;
  nursesList?: NurseResponse[];
  selectedGroupId: string | null;
  usePreview?: boolean;
}

function ModalWeekOffFullNurse({
  onClose,
  weeklyOffSetYear,
  weeklyOffSetMonth,
  nursesList,
  selectedGroupId,
  usePreview = false,
}: ModalWeekOffFullNurseProps) {
  function secondsToTimeString(seconds: number | null): string {
    if (seconds == null) return "";
    const hours = String(Math.floor(seconds / 3600)).padStart(2, "0");
    const minutes = String(Math.floor((seconds % 3600) / 60)).padStart(2, "0");
    return `${hours}:${minutes}`;
  }

  const loginUser = useLoginUser();

  const isAdmin = Boolean(
    loginUser?.EmpAuthGbn === "ADM" || loginUser?.is_master_admin
  );

  // 주휴 shift 정보 가져오기 위함
  const targetGroupId = isAdmin ? selectedGroupId || undefined : undefined;
  const isEnabled = !isAdmin || (isAdmin && !!selectedGroupId);

  const { data: shiftsData, isPending: shiftsPending } = useQuery({
    queryKey: ["shifts", targetGroupId],
    queryFn: () => req.shifts(targetGroupId),
    enabled: isEnabled,
    select: (data) => {
      const sorted = [...data].sort((a, b) => a.sequence - b.sequence);
      return sorted.map((shift) => ({
        ...shift,
        start_time: secondsToTimeString(shift.start_time),
        end_time: secondsToTimeString(shift.end_time),
      }));
    },
    staleTime: 5 * 60 * 1000, // 5분
  });

  const weeklyOffShiftInfo = useMemo(() => {
    if (!shiftsData) return [];
    return shiftsData.filter((shift) => shift.shift_id === "주");
  }, [shiftsData]);

  const weeklyOffQueryEnabled =
    !!weeklyOffSetYear && !!weeklyOffSetMonth && usePreview;

  const { data: weeklyOffData, isPending: weeklyOffPending } = useQuery({
    queryKey: [
      "weeklyOffNurses",
      weeklyOffSetYear,
      weeklyOffSetMonth,
      targetGroupId,
    ],
    queryFn: () =>
      weeklyOffNurses({
        year: weeklyOffSetYear!,
        month: weeklyOffSetMonth!,
        groupId: targetGroupId,
      }),
    /** 미리보기일 때만 API 호출 — 목록은 상위 `nursesList`로 충분 */
    enabled: weeklyOffQueryEnabled,
  });

  const isPanelLoading =
    (isEnabled && shiftsPending) || (weeklyOffQueryEnabled && weeklyOffPending);

  const mergedNursesList = useMemo(() => {
    if (!nursesList) return [];

    if (usePreview && weeklyOffData?.items) {
      return nursesList.map((nurse) => {
        const match = weeklyOffData.items.find(
          (item) => item.nurse_id === nurse.nurse_id
        );
        if (match) {
          return {
            ...nurse,
            weekly_off_weekday: match.preview_weekday ?? undefined,
          };
        }
        return nurse;
      });
    }

    return nursesList;
  }, [nursesList, weeklyOffData, usePreview]);

  const [holidayDates, setHolidayDates] = useState<string[]>([]);

  async function loadHolidayDates(year: number, month: number) {
    try {
      const res_holiday = await getHolidayDates(year, month);
      setHolidayDates(res_holiday);
    } catch (err) {
      setHolidayDates([]);
      console.error("holiday error" + err);
      throw new Error("휴일 정보를 가져오지 못했습니다.");
    }
  }

  useEffect(() => {
    if (weeklyOffSetYear != null && weeklyOffSetMonth != null) {
      void loadHolidayDates(weeklyOffSetYear, weeklyOffSetMonth);
    }
  }, [weeklyOffSetYear, weeklyOffSetMonth]);

  return (
    <CustomModal.Panel
      onClose={onClose}
      dismissOnOverlay
      header
      headerTitle="전체 근무자 주휴 배치 현황"
      isLoading={isPanelLoading}
      loadingMsg="전체 근무자 주휴 정보를 불러오는 중입니다."
      // className="border border-[#D6D6DE] bg-white shadow-[2px_2px_4px_2px_rgba(0,0,0,0.2)]"
    >
      <section className=" -m-5 px-5 pt-4 min-w-0 select-none rounded-lg bg-white">
        <div className="">
          <div className="bg-background sticky top-13 z-9">
            <ModalWeeklyOffTableHeader
              currentYear={weeklyOffSetYear}
              currentMonth={weeklyOffSetMonth}
              holidayDates={holidayDates}
              nursesCount={nursesList?.length ?? 0}
            />
          </div>

          <div className="z-0">
            <ModalWeeklyOffTableBodyView
              shiftInfo={weeklyOffShiftInfo ?? []}
              nursesList={mergedNursesList}
              year={weeklyOffSetYear}
              month={weeklyOffSetMonth}
            />
          </div>
        </div>
      </section>
    </CustomModal.Panel>
  );
}

export default ModalWeekOffFullNurse;
